import React from 'react';
import { get, partial } from 'lodash';
import {
  ApiKeys,
  BankPaymentSource,
  PaymentDetailsBank,
  PaymentProcessor,
  PaymentSourceInput,
  PaymentSourceType,
  SupportedPaymentMethod,
} from '@zapier/payment/src/types';
import BankAccountForm from '@zapier/payment/src/components/BankAccountForm';
import PaymentInfo from '@zapier/payment/src/components/PaymentInfo';
import { useSize } from '@zapier/payment/src/hooks/useSize';

import { Button, Text } from '@zapier/design-system';
import PaymentMethod from '../PaymentMethod';
import PaymentMethodSelect from '../PaymentMethodSelect';
import { Currency } from '@zapier/currency';

type Props = {
  /** API keys */
  apiKeys: ApiKeys;
  /** Currently select method */
  currentMethod: PaymentSourceType | null;
  /** Error message to show */
  error?: string | null;
  /** Hide switch component button */
  hideSwitch: boolean;
  /** Show editor */
  isEditing: boolean;
  /** Show loading */
  isEditingInFlight: boolean;
  /** Call to cancel edit */
  onCancelEdit: () => void;
  /** Call to start edit */
  onEdit: () => void;
  /** Call when editor throws an error */
  onErrorEdit: (error: Error) => void;
  /** Call to save current edit */
  onSaveEdit: (b: PaymentSourceInput) => void;
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceType) => void;
  /** User's currently saved bank details */
  savedSource: BankPaymentSource | null;
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>;
  /** The array of payment methods selectable from the "Switch Payment Method" dropdown */
  switchMethodOptions: SupportedPaymentMethod[];
  /** The selected currency */
  selectedCurrency: Currency;
};

const renderLabel = (props: Props) =>
  props.savedSource && !props.isEditing ? (
    <PaymentInfo paymentSource={props.savedSource} />
  ) : (
    <Text tag="h3" type="paragraph3Bold">
      Add Bank Transfer (US only)
    </Text>
  );

const renderAside = (props: Props, isSmall: boolean) => {
  return props.isEditing ? (
    props.hideSwitch ? null : (
      <PaymentMethodSelect
        currentMethod={props.currentMethod}
        onChange={props.onSelectSource}
        supportedMethods={props.switchMethodOptions}
      />
    )
  ) : (
    <Button color="secondary" onClick={props.onEdit}>
      {props.savedSource ? `Edit${!isSmall ? ' Payment Method' : ''}` : 'Add'}
    </Button>
  );
};

const PaymentMethodBank: React.FC<Props> = props => {
  const size = useSize();

  // @ts-ignore - PaymentDetailsBank and what is being `get`'d here is not compatible
  const bankDetails: PaymentDetailsBank = get(props, [
    'savedSource',
    'details',
  ]);

  const bankMethod = props.supportedMethods.find(
    m => m.method === PaymentSourceType.bank
  );

  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={partial(renderAside, props, size === 'small')}
      renderLabel={partial(renderLabel, props)}
    >
      <BankAccountForm
        braintreeAPIKey={props?.apiKeys?.braintree ?? ''}
        error={props.error}
        isEditingInFlight={props.isEditingInFlight}
        onCancelEdit={props.onCancelEdit}
        onError={props.onErrorEdit}
        onSave={props.onSaveEdit}
        paymentProcessor={get(
          bankMethod,
          'paymentProcessor',
          PaymentProcessor.braintree
        )}
        savedAccount={bankDetails}
        selectedCurrency={props.selectedCurrency}
        supportedSubmethods={props.supportedMethods
          .filter(m => m.method === PaymentSourceType.bank)
          .map(m => m.submethod)}
      />
    </PaymentMethod>
  );
};

export default PaymentMethodBank;
